import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Drawer,
  Dropdown,
  FormControl,
  FormLabel,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
  Textarea,
} from "@mui/joy";
import { api, downloadApiBlob } from "../../lib/api";
import { useDebounce } from "../../hooks/useDebounce";
import {
  ResponsiveTable,
  type ResponsiveColumn,
} from "../../components/common/ResponsiveTable";
import { ContractCreateModal } from "../../components/crm/contracts/ContractCreateModal";
import { PdfViewerModal } from "../../components/crm/contracts/PdfViewerModal";
import type { Contract } from "../../types";
import type { ContractWorkflow } from "../../components/crm/contracts/contractWorkflow";
import { localCalendarDate } from "../../lib/calendarDate";
import {
  csvCell,
  retryableEmails,
  selectionOnPage,
  type ReminderResult,
} from "./contractListHelpers";

type Row = Contract & {
  attention: {
    label: string;
    signed: number;
    total: number;
    can_remind: boolean;
  };
};
type Reviewed = Omit<Row, "recipients"> & {
  review_version: string;
  recipients: { email: string; name: string; delivery_email?: string }[];
};
type Outcome = { message: string; retry: boolean; results?: ReminderResult[] };
type Preferences = {
  layout?: "table" | "cards";
  views: { name: string; query: string }[];
  columns: string[];
};
const defaults: Preferences = {
  views: [],
  columns: [
    "customer",
    "status",
    "owner",
    "sent",
    "expiration",
  ],
};
const money = (value: unknown) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
const date = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleDateString() : "—";
const message = (error: any) =>
  error?.responseData?.error ||
  error?.message ||
  "Action could not be completed.";

function Preview({ id, close }: { id: number; close: () => void }) {
  const [pdf, setPdf] = useState(false);
  const detail = useQuery({
    queryKey: ["contract-detail", String(id)],
    queryFn: async () =>
      (await api.get<Contract>(`/api/crm/contracts/${id}/`)).data,
  });
  const workflow = useQuery({
    queryKey: ["contract-workflow", String(id)],
    queryFn: async () =>
      (await api.get<ContractWorkflow>(`/api/crm/contracts/${id}/workflow/`))
        .data,
  });
  const c = detail.data;
  return (
    <Drawer open onClose={close} anchor="right" size="md">
      <Box sx={{ p: 3, overflow: "auto" }}>
        <Button onClick={close} variant="plain">
          Close preview
        </Button>
        {detail.isPending ? (
          <CircularProgress />
        ) : detail.isError || !c ? (
          <Alert color="danger">
            Preview unavailable.{" "}
            <Button onClick={() => detail.refetch()}>Retry</Button>
          </Alert>
        ) : (
          <Stack gap={2}>
            <Typography level="h3">{c.title}</Typography>
            <Typography>
              {c.contract_number} · {money(c.total_value)}
            </Typography>
            <Chip>{c.status}</Chip>
            <Typography component={Link} to={`/crm/contacts/${c.customer}`}>
              {c.customer_name}
            </Typography>
            <Typography level="title-lg">Signer progress</Typography>
            {workflow.data?.recipients.map((r) => (
              <Typography key={r.email}>
                {r.name || r.email} · {r.next_step}
              </Typography>
            ))}
            {workflow.isError && (
              <Alert color="warning">
                Signer status unavailable.{" "}
                <Button onClick={() => workflow.refetch()}>Retry</Button>
              </Alert>
            )}
            <Button disabled={!c.pdf_url} onClick={() => setPdf(true)}>
              Preview document
            </Button>
            <Button component={Link} to={`/crm/contracts/${id}`}>
              Open full contract
            </Button>
            {c.pdf_url && (
              <PdfViewerModal
                open={pdf}
                onClose={() => setPdf(false)}
                pdfUrl={c.completed_pdf_url || c.pdf_url}
                title={c.title}
                fields={c.completed_pdf_url ? undefined : c.contract_fields}
                fieldValues={c.contract_data}
              />
            )}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

export const ContractsPage = () => {
  const [params, setParams] = useSearchParams();
  const query = params.toString();
  const debounced = useDebounce(query, 250);
  const changing = query !== debounced;
  const page = Math.max(1, Number(params.get("page")) || 1);
  const client = useQueryClient(),
    navigate = useNavigate();
  const [selected, setSelected] = useState<number[]>([]),
    [preview, setPreview] = useState<number | null>(null),
    [create, setCreate] = useState(false),
    [edit, setEdit] = useState<Row | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState("");
  const lock = useRef(false);
  const [batch, setBatch] = useState<Reviewed[] | null>(null),
    [operation, setOperation] = useState("reminders"),
    [outcomes, setOutcomes] = useState<Record<number, Outcome>>({}),
    [prefs, setPrefs] = useState<Preferences>(defaults),
    [scope, setScope] = useState(""),
    [name, setName] = useState(""),
    [optionSearch, setOptionSearch] = useState("");
  const restored = useRef(false);
  const [copy, setCopy] = useState<Row | null>(null),
    [copyEmail, setCopyEmail] = useState(""),
    [copyMessage, setCopyMessage] = useState(""),
    [copyResult, setCopyResult] = useState("");
  const update = (
    values: Record<string, string | number | null>,
    reset = true,
  ) => {
    const next = new URLSearchParams(params);
    if (reset) next.delete("page");
    Object.entries(values).forEach(([key, value]) => {
      if (value === null || value === "") next.delete(key);
      else next.set(key, String(value));
    });
    setParams(next);
  };
  const listing = useQuery({
    queryKey: ["contract-list", debounced],
    queryFn: async () =>
      (
        await api.get<{ count: number; page: number; results: Row[] }>(
          `/api/crm/contracts/?${debounced}`,
        )
      ).data,
  });
  const report = useQuery({
    queryKey: ["contract-report", debounced],
    queryFn: async () =>
      (
        await api.get<{
          total: number;
          draft: number;
          pending: number;
          completed: number;
          sent_cohort: number;
          completed_cohort: number;
          timed_completions: number;
          average_days: number | null;
          completion_rate: number | null;
          basis: string;
        }>(`/api/crm/contracts/worklist-report/?${debounced}`)
      ).data,
  });
  const options = useQuery({
    queryKey: ["contract-options", optionSearch],
    queryFn: async () =>
      (
        await api.get<{
          scope: string;
          owners: { id: number; first_name: string; last_name: string }[];
          customers: { id: number; first_name: string; last_name: string }[];
          opportunities: { id: number; name: string }[];
        }>(
          `/api/crm/contracts/worklist-options/?search=${encodeURIComponent(optionSearch)}`,
        )
      ).data,
  });
  useEffect(() => {
    setSelected([]);
  }, [query]);
  useEffect(() => {
    if (!changing && listing.data?.page && listing.data.page !== page)
      update({ page: listing.data.page }, false);
  }, [listing.data?.page, page, changing]);
  useEffect(() => {
    const key = options.data?.scope;
    if (!key || key === scope) return;
    try {
      const saved = JSON.parse(
        localStorage.getItem(`apexos:contract-worklists:${key}`) || "null",
      );
      if (saved && Array.isArray(saved.views) && Array.isArray(saved.columns))
        setPrefs(saved);
    } catch {}
    setScope(key);
  }, [options.data?.scope, scope]);
  useEffect(() => {
    if (scope)
      try {
        localStorage.setItem(
          `apexos:contract-worklists:${scope}`,
          JSON.stringify(prefs),
        );
      } catch {}
  }, [scope, prefs]);
  const rows = listing.data?.results || [];
  const rowIds = rows.map((row) => row.id).join(",");
  useEffect(() => setSelected([]), [rowIds]);
  useEffect(() => {
    if (restored.current || !scope || !listing.data || changing) return;
    restored.current = true;
    try {
      const id = sessionStorage.getItem(`apexos:contract-position:${scope}`);
      if (id)
        requestAnimationFrame(() =>
          document
            .querySelector(`[data-contract-id="${Number(id)}"]`)
            ?.scrollIntoView({ block: "center" }),
        );
    } catch {}
  }, [scope, listing.data, changing]);
  const remember = (id: number) => {
    try {
      sessionStorage.setItem(`apexos:contract-position:${scope}`, String(id));
    } catch {}
  };
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["contract-list"] }),
      client.invalidateQueries({ queryKey: ["contract-report"] }),
      client.invalidateQueries({ queryKey: ["contract-workflow"] }),
    ]);
  };
  const run = async (label: string, fn: () => Promise<unknown>) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(label);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy("");
      lock.current = false;
    }
  };
  const review = (ids: number[], action = "reminders") =>
    run("review", async () => {
      const response = await api.post<{ results: Reviewed[] }>(
        `/api/crm/contracts/batch-review/?${query}`,
        { ids },
      );
      setBatch(response.data.results);
      setOutcomes({});
      setOperation(action);
    });
  const execute = async (retry = false) =>
    run("batch", async () => {
      if (!batch) return;
      if (operation === "export") {
        const data = [
          [
            "Contract",
            "Customer",
            "Opportunity",
            "Value",
            "Status",
            "Sent",
            "Expiration",
            "Recipients",
          ],
          ...batch.map((r) => [
            r.contract_number,
            r.customer_name,
            r.opportunity_name,
            r.total_value,
            r.status,
            r.sent_date,
            r.expiration_date,
            r.recipients.map((p) => p.email).join("; "),
          ]),
        ];
        const url = URL.createObjectURL(
          new Blob(
            [data.map((row) => row.map(csvCell).join(",")).join("\r\n")],
            { type: "text/csv" },
          ),
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = "contracts.csv";
        link.click();
        URL.revokeObjectURL(url);
        setOutcomes(
          Object.fromEntries(
            batch.map((r) => [
              r.id,
              { message: "Included in export", retry: false },
            ]),
          ),
        );
        return;
      }
      for (const row of batch) {
        if (retry && !outcomes[row.id]?.retry) continue;
        let outcome: Outcome;
        try {
          if (operation === "reminders") {
            const emails = retry
              ? retryableEmails(outcomes[row.id]?.results || [])
              : row.recipients.map((r) => r.email);
            if (!emails.length) {
              outcome = {
                message: "No eligible unsigned recipients",
                retry: false,
              };
            } else {
              const response = await api.post<{ results: ReminderResult[] }>(
                `/api/crm/contracts/${row.id}/reviewed-reminders/`,
                {
                  review_version: row.review_version,
                  emails,
                  delivery_emails: Object.fromEntries(
                    row.recipients
                      .filter((r) => emails.includes(r.email))
                      .map((r) => [
                        r.email,
                        (r.delivery_email ?? r.email).trim(),
                      ]),
                  ),
                },
              );
              const results = response.data.results;
              outcome = {
                message: `${results.filter((r) => r.sent === true).length} of ${results.length} accepted by provider`,
                results,
                retry: results.some((r) => r.sent === false),
              };
            }
          } else if (operation === "downloads") {
            if (!row.pdf_url)
              outcome = { message: "No PDF available", retry: false };
            else {
              await downloadApiBlob(row.pdf_url, `${row.contract_number}.pdf`);
              outcome = { message: "PDF download requested", retry: false };
            }
          } else if (operation === "delete") {
            await api.delete(`/api/crm/contracts/${row.id}/?updated_at=${encodeURIComponent(row.updated_at)}`);
            outcome = { message: "Contract deleted", retry: false };
          } else {
            await api.post(
              `/api/crm/contracts/${row.id}/${operation === "restore" ? "unarchive" : "archive"}/`,
              {},
            );
            outcome = {
              message: operation === "restore" ? "Restored" : "Archived",
              retry: false,
            };
          }
        } catch (e: any) {
          outcome = {
            message:
              operation === "reminders"
                ? `Not confirmed: ${message(e)}. Review activity before retrying.`
                : message(e),
            retry:
              operation === "downloads" ||
              operation === "archive" ||
              operation === "restore",
          };
        }
        setOutcomes((old) => ({ ...old, [row.id]: outcome }));
      }
      await refresh();
      setSelected([]);
    });
  const columns: ResponsiveColumn<Row>[] = [
    {
      key: "select",
      width: "44px",
      header: (
        <Checkbox
          slotProps={{ input: { "aria-label": "Select this page" } }}
          checked={
            rows.length > 0 && rows.every((r) => selected.includes(r.id))
          }
          indeterminate={
            selected.length > 0 && !rows.every((r) => selected.includes(r.id))
          }
          onChange={(e) =>
            setSelected(e.target.checked ? rows.map((r) => r.id) : [])
          }
        />
      ),
      render: (r) => (
        <Checkbox
          slotProps={{ input: { "aria-label": `Select ${r.contract_number}` } }}
          checked={selected.includes(r.id)}
          onChange={() =>
            setSelected((old) =>
              old.includes(r.id)
                ? old.filter((id) => id !== r.id)
                : [...old, r.id],
            )
          }
        />
      ),
    },
    {
      key: "contract",
      width: "28%",
      header: "Contract",
      sortKey: "contract_number",
      mobileVariant: "header",
      render: (r) => (
        <Box data-contract-id={r.id}>
          <Typography
            component={Link}
            to={`/crm/contracts/${r.id}`}
            onClick={() => remember(r.id)}
          >
            {r.contract_number}
          </Typography>
          <Typography level="body-sm">{r.title}</Typography>
        </Box>
      ),
    },
    {
      key: "customer",
      header: "Customer",
      sortKey: "customer__first_name",
      render: (r) => r.customer_name,
    },
    {
      key: "status",
      header: "Status",
      sortKey: "status",
      render: (r) => (
        <Chip
          size="sm"
          color={r.status === "completed" ? "success" : "neutral"}
        >
          {r.status.replaceAll("_", " ")}
        </Chip>
      ),
    },
    {
      key: "owner",
      header: "Salesperson",
      sortKey: "sales_rep__first_name",
      render: (r) => r.sales_rep_name || "Unassigned",
    },
    {
      key: "opportunity",
      header: "Opportunity",
      render: (r) => r.opportunity_name || "—",
    },
    {
      key: "sent",
      header: "Sent",
      sortKey: "sent_date",
      render: (r) => date(r.sent_date),
    },
    {
      key: "expiration",
      header: "Expiration",
      sortKey: "expiration_date",
      render: (r) => date(r.expiration_date),
    },
  ];
  const rowActions = (r: Row) => (
    <Stack direction="row" gap={1}>
      <Button
        size="sm"
        variant="plain"
        onClick={() => {
          remember(r.id);
          setPreview(r.id);
        }}
      >
        Preview
      </Button>
      <Dropdown>
        <MenuButton size="sm" aria-label={`Actions for ${r.contract_number}`}>
          More
        </MenuButton>
        <Menu>
          <MenuItem
            onClick={() => {
              remember(r.id);
              navigate(`/crm/contracts/${r.id}`);
            }}
          >
            Open / review signing
          </MenuItem>
          {r.status === "draft" && (
            <MenuItem
              onClick={() => {
                setEdit(r);
                setCreate(true);
              }}
            >
              Edit draft
            </MenuItem>
          )}
          <MenuItem
            disabled={!r.pdf_url}
            onClick={() =>
              run("download", () =>
                downloadApiBlob(r.pdf_url!, `${r.contract_number}.pdf`),
              )
            }
          >
            Download PDF
          </MenuItem>
          {r.status === "completed" && r.pdf_url && (
            <MenuItem
              onClick={() => {
                setCopy(r);
                setCopyEmail(r.customer_email || "");
                setCopyMessage("");
                setCopyResult("");
                setError("");
              }}
            >
              Send completed copy
            </MenuItem>
          )}
          {r.attention.can_remind && (
            <MenuItem onClick={() => review([r.id])}>Review reminders</MenuItem>
          )}
          <MenuItem
            onClick={() =>
              review([r.id], r.is_archived ? "restore" : "archive")
            }
          >
            {r.is_archived ? "Restore" : "Archive"}
          </MenuItem>
          <MenuItem color="danger" onClick={() => review([r.id], "delete")}>
            Delete contract
          </MenuItem>
        </Menu>
      </Dropdown>
    </Stack>
  );
  const count = listing.data?.count || 0;
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, width: "100%" }}>
      <Stack
        direction="row"
        gap={2}
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography level="h2">Contracts</Typography>
          <Typography>
            Find the next signing step and keep paperwork moving.
          </Typography>
        </Box>
        <Stack direction="row" gap={1}>
          <Button
            component={Link}
            to="/crm/contracts/templates"
            variant="outlined"
          >
            Templates
          </Button>
          <Button
            onClick={() => {
              setEdit(null);
              setCreate(true);
            }}
          >
            New contract
          </Button>
        </Stack>
      </Stack>
      {error && (
        <Alert color="danger" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}
      <CollapsibleFilters><Card sx={{ my: 2 }}>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Input
            placeholder="Search contracts…"
            value={params.get("search") || ""}
            onChange={(e) => update({ search: e.target.value })}
            sx={{ flex: "1 1 220px" }}
          />
          <Select
            value={params.get("worklist") || ""}
            onChange={(_, value) =>
              update({ worklist: value || "", status: "" })
            }
          >
            <Option value="">All contracts</Option>
            <Option value="attention">Needs attention</Option>
            <Option value="drafts">Drafts</Option>
            <Option value="awaiting">Awaiting signatures</Option>
            <Option value="review">Pending review</Option>
            <Option value="expiring">Expiring in 7 days / overdue</Option>
            <Option value="completed">Completed</Option>
          </Select>
          <Select
            value={params.get("status") || ""}
            onChange={(_, value) =>
              update({ status: value || "", worklist: "" })
            }
          >
            <Option value="">Any status</Option>
            {[
              "draft",
              "sent",
              "viewed",
              "in_progress",
              "pending_review",
              "completed",
              "expired",
              "cancelled",
            ].map((status) => (
              <Option key={status} value={status}>
                {status.replaceAll("_", " ")}
              </Option>
            ))}
          </Select>
          <Select
            value={params.get("is_archived") || "false"}
            onChange={(_, value) => update({ is_archived: value || "false" })}
          >
            <Option value="false">Active records</Option>
            <Option value="true">Archived records</Option>
          </Select>
          <Select
            value={params.get("sales_rep") || ""}
            onChange={(_, value) => update({ sales_rep: value || "" })}
          >
            <Option value="">All salespeople</Option>
            <Option value="me">Assigned to me</Option>
            <Option value="none">Unassigned</Option>
            {options.data?.owners.map((o) => (
              <Option key={o.id} value={String(o.id)}>
                {o.first_name} {o.last_name}
              </Option>
            ))}
          </Select>
          <Button
            variant="plain"
            onClick={() => setParams(new URLSearchParams())}
          >
            Clear filters
          </Button>
        </Stack>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Input
            placeholder="Find customer or opportunity"
            value={optionSearch}
            onChange={(e) => setOptionSearch(e.target.value)}
          />
          <Select
            value={params.get("customer") || ""}
            onChange={(_, value) => update({ customer: value || "" })}
          >
            <Option value="">All customers</Option>
            {params.get("customer") &&
              !options.data?.customers.some(
                (c) => String(c.id) === params.get("customer"),
              ) && (
                <Option value={params.get("customer")!}>
                  Selected customer #{params.get("customer")}
                </Option>
              )}
            {options.data?.customers.map((c) => (
              <Option key={c.id} value={String(c.id)}>
                {c.first_name} {c.last_name}
              </Option>
            ))}
          </Select>
          <Select
            value={params.get("opportunity") || ""}
            onChange={(_, value) => update({ opportunity: value || "" })}
          >
            <Option value="">All opportunities</Option>
            {params.get("opportunity") &&
              !options.data?.opportunities.some(
                (o) => String(o.id) === params.get("opportunity"),
              ) && (
                <Option value={params.get("opportunity")!}>
                  Selected opportunity #{params.get("opportunity")}
                </Option>
              )}
            {options.data?.opportunities.map((o) => (
              <Option key={o.id} value={String(o.id)}>
                {o.name}
              </Option>
            ))}
          </Select>
          {[
            ["value_min", "Minimum value"],
            ["value_max", "Maximum value"],
          ].map(([key, label]) => (
            <Input
              type="number"
              placeholder={label}
              key={key}
              value={params.get(key) || ""}
              onChange={(e) => update({ [key]: e.target.value })}
              sx={{ width: 150 }}
            />
          ))}
        </Stack>
        <Stack direction="row" gap={1} flexWrap="wrap">
          {[
            ["date_from", "Created from"],
            ["date_to", "Created through"],
            ["sent_from", "Sent from"],
            ["sent_to", "Sent through"],
            ["expires_from", "Expires from"],
            ["expires_to", "Expires through"],
          ].map(([key, label]) => (
            <FormControl key={key}>
              <FormLabel>{label}</FormLabel>
              <Input
                type="date"
                value={params.get(key) || ""}
                onChange={(e) => update({ [key]: e.target.value })}
              />
            </FormControl>
          ))}
          <Button
            variant="plain"
            onClick={() => {
              const from = new Date();
              from.setDate(from.getDate() - 30);
              update({
                date_from: localCalendarDate(from),
                date_to: localCalendarDate(),
              });
            }}
          >
            Created in last 30 days
          </Button>
        </Stack>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Select
            placeholder="Saved worklists"
            onChange={(_, value) => {
              if (value !== null) setParams(new URLSearchParams(value));
            }}
          >
            {prefs.views.map((view) => (
              <Option key={view.name} value={view.query}>
                {view.name}
              </Option>
            ))}
          </Select>
          <Input
            placeholder="Name this worklist"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button
            disabled={!name.trim() || !scope}
            onClick={() => {
              setPrefs((old) => ({
                ...old,
                views: [
                  ...old.views.filter((v) => v.name !== name.trim()),
                  { name: name.trim(), query },
                ],
              }));
              setName("");
            }}
          >
            Save worklist
          </Button>
          <Button
            variant="outlined"
            onClick={() =>
              setPrefs((old) => ({
                ...old,
                layout: old.layout === "cards" ? "table" : "cards",
              }))
            }
          >
            {prefs.layout === "cards" ? "Table view" : "Card view"}
          </Button>
          <Dropdown>
            <MenuButton>Columns</MenuButton>
            <Menu>
              {[
                "customer",
                "status",
                "owner",
                "opportunity",
                "sent",
                "expiration",
              ].map((key) => (
                <MenuItem
                  key={key}
                  onClick={() =>
                    setPrefs((old) => ({
                      ...old,
                      columns: old.columns.includes(key)
                        ? old.columns.filter((c) => c !== key)
                        : [...old.columns, key],
                    }))
                  }
                >
                  {prefs.columns.includes(key) ? "✓ " : ""}
                  {key}
                </MenuItem>
              ))}
            </Menu>
          </Dropdown>
        </Stack>
      </Card></CollapsibleFilters>
      <Card sx={{ mb: 2 }}>
        <Typography level="title-lg">
          Matching contracts · selected creation period
        </Typography>
        {report.isError ? (
          <Alert color="warning">
            Report unavailable.{" "}
            <Button onClick={() => report.refetch()}>Retry report</Button>
          </Alert>
        ) : report.isPending || changing ? (
          <CircularProgress size="sm" />
        ) : (
          <>
            <Stack direction="row" gap={3} flexWrap="wrap">
              <Typography>Total {report.data.total}</Typography>
              <Typography>Drafts {report.data.draft}</Typography>
              <Typography>Awaiting {report.data.pending}</Typography>
              <Typography>Completed {report.data.completed}</Typography>
              <Typography>
                Completion rate{" "}
                {report.data.completion_rate === null
                  ? "—"
                  : `${report.data.completion_rate.toFixed(1)}%`}{" "}
                ({report.data.completed_cohort}/{report.data.sent_cohort} sent)
              </Typography>
              <Typography>
                Send to sign{" "}
                {report.data.average_days === null
                  ? "—"
                  : `${report.data.average_days.toFixed(1)} days`}{" "}
                ({report.data.timed_completions} timed completions)
              </Typography>
            </Stack>
            <Typography level="body-xs">{report.data.basis}</Typography>
          </>
        )}
      </Card>
      <Stack direction="row" gap={1} sx={{ mb: 2 }}>
        <Typography>{selected.length} selected on this page</Typography>
        <Button
          disabled={!selected.length || !!busy || changing}
          onClick={() =>
            review(
              selectionOnPage(
                selected,
                rows.map((row) => row.id),
              ),
            )
          }
        >
          Review selected
        </Button>
      </Stack>
      {listing.isError ? (
        <Alert color="danger">
          Contracts unavailable.{" "}
          <Button onClick={() => listing.refetch()}>Retry</Button>
        </Alert>
      ) : listing.isPending || changing ? (
        <CircularProgress />
      ) : prefs.layout === "cards" ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2,minmax(0,1fr))",
              xl: "repeat(3,minmax(0,1fr))",
            },
            gap: 2,
          }}
        >
          {rows.map((row) => (
            <Card key={row.id} data-contract-id={row.id}>
              <Stack direction="row" gap={1}>
                <Checkbox
                  slotProps={{
                    input: { "aria-label": `Select ${row.contract_number}` },
                  }}
                  checked={selected.includes(row.id)}
                  onChange={() =>
                    setSelected((old) =>
                      old.includes(row.id)
                        ? old.filter((id) => id !== row.id)
                        : [...old, row.id],
                    )
                  }
                />
                <Typography
                  component={Link}
                  to={`/crm/contracts/${row.id}`}
                  onClick={() => remember(row.id)}
                >
                  {row.contract_number}
                </Typography>
              </Stack>
              <Typography level="title-lg">{row.title}</Typography>
              <Typography>
                {row.customer_name}
              </Typography>
              <Chip>{row.status}</Chip>
              {rowActions(row)}
            </Card>
          ))}
        </Box>
      ) : (
        <ResponsiveTable<Row>
          rows={changing ? [] : rows}
          columns={columns.filter(
            (c) =>
              ["select", "contract"].includes(c.key) ||
              prefs.columns.includes(c.key),
          )}
          getRowKey={(r) => r.id}
          sort={{
            key: (params.get("ordering") || "-created_at")
              .split(",")[0]
              .replace(/^-/, ""),
            direction: (params.get("ordering") || "-created_at").startsWith("-")
              ? "desc"
              : "asc",
          }}
          onSortChange={(sort) =>
            update({
              ordering: sort
                ? `${sort.direction === "desc" ? "-" : ""}${sort.key},id`
                : "-created_at,id",
            })
          }
          rowActions={rowActions}
          actionsWidth="160px"
          cardBreakpoint={1200}
        />
      )}
      <Stack direction="row" gap={1} sx={{ my: 2 }}>
        <Button
          disabled={page === 1 || changing}
          onClick={() => update({ page: page - 1 }, false)}
        >
          Previous
        </Button>
        <Typography>
          Page {page} of {Math.max(1, Math.ceil(count / 20))} · {count}{" "}
          contracts
        </Typography>
        <Button
          disabled={page * 20 >= count || changing}
          onClick={() => update({ page: page + 1 }, false)}
        >
          Next
        </Button>
      </Stack>
      {preview && (
        <Preview key={preview} id={preview} close={() => setPreview(null)} />
      )}
      {create && (
        <ContractCreateModal
          open
          onClose={() => setCreate(false)}
          contract={edit}
          onSave={async (data) => {
            if (edit) await api.patch(`/api/crm/contracts/${edit.id}/`, data);
            else await api.post("/api/crm/contracts/", data);
            setCreate(false);
            await refresh();
          }}
        />
      )}
      {copy && (
        <Modal
          open
          onClose={() => {
            if (!busy) setCopy(null);
          }}
        >
          <ModalDialog sx={{ width: "min(600px,95vw)" }}>
            <Typography level="h3">Send completed copy</Typography>
            <Typography>
              {copy.contract_number} · {copy.customer_name}
            </Typography>
            {error && <Alert color="danger">{error}</Alert>}
            {copyResult ? (
              <Alert color="success">{copyResult}</Alert>
            ) : (
              <>
                <FormControl>
                  <FormLabel>Recipient email</FormLabel>
                  <Input
                    type="email"
                    value={copyEmail}
                    onChange={(e) => setCopyEmail(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Message (optional)</FormLabel>
                  <Textarea
                    value={copyMessage}
                    onChange={(e) => setCopyMessage(e.target.value)}
                  />
                </FormControl>
                <Button
                  disabled={!!busy || !copyEmail.trim()}
                  loading={busy === "copy"}
                  onClick={() =>
                    run("copy", async () => {
                      const response = await api.post<{ email: string }>(
                        `/api/crm/contracts/${copy.id}/send-copy/`,
                        { email: copyEmail.trim(), message: copyMessage },
                      );
                      setCopyResult(
                        `Email provider accepted the copy for ${response.data.email}.`,
                      );
                    })
                  }
                >
                  Send this copy
                </Button>
              </>
            )}
            <Button
              disabled={!!busy}
              variant="plain"
              onClick={() => setCopy(null)}
            >
              Close
            </Button>
          </ModalDialog>
        </Modal>
      )}
      {batch && (
        <Modal
          open
          onClose={() => {
            if (!busy) setBatch(null);
          }}
        >
          <ModalDialog
            sx={{
              width: "min(1000px,95vw)",
              maxHeight: "94vh",
              overflow: "auto",
            }}
          >
            <Typography level="h3">
              Review {batch.length} selected {batch.length === 1 ? "contract" : "contracts"}
            </Typography>
            <Typography>
              {operation === "reminders" && `${batch.reduce((n, r) => n + r.recipients.length, 0)} eligible unsigned recipients. `}
              Actions apply only to the records below.
            </Typography>
            <Select
              disabled={!!busy || Object.keys(outcomes).length > 0}
              value={operation}
              onChange={(_, value) => setOperation(value || "reminders")}
            >
              <Option value="reminders">Send reviewed reminders</Option>
              <Option value="downloads">Download PDFs</Option>
              <Option value="export">Export CSV</Option>
              <Option value="archive">Archive records</Option>
              <Option value="restore">Restore records</Option>
              <Option value="delete">Delete entire contracts</Option>
            </Select>
            {operation === "delete" && (
              <Alert color="danger">
                Permanently delete the selected contracts, including their versions,
                signatures and activity history. Signing links will stop working.
                Linked customers, proposals, jobs and separately saved documents remain.
                Download any contract records you need first. This cannot be undone.
              </Alert>
            )}
            {operation === "downloads" && (
              <Typography>
                The browser may request permission to save multiple PDFs.
              </Typography>
            )}
            {error && <Alert color="danger">{error}</Alert>}
            {batch.map((r) => (
              <Card key={r.id}>
                <Typography level="title-md">
                  {r.contract_number} · {r.customer_name} · {r.status}
                </Typography>
                {operation === "reminders" && <Typography>
                  {r.recipients.length
                    ? r.recipients
                        .map((p) => `${p.name} <${p.email}>`)
                        .join(", ")
                    : "No eligible reminder recipients"}
                </Typography>}
                {operation === "reminders" &&
                  r.recipients
                        .map((recipient) => (
                    <FormControl key={recipient.email} required>
                      <FormLabel>
                        Delivery email for {recipient.name || recipient.email}
                      </FormLabel>
                      <Input
                        type="email"
                        value={recipient.delivery_email ?? recipient.email}
                        disabled={!!busy || !!outcomes[r.id]}
                        onChange={(e) =>
                          setBatch(
                            (rows) =>
                              rows?.map((row) =>
                                row.id === r.id
                                  ? {
                                      ...row,
                                      recipients: row.recipients.map(
                                        (person) =>
                                          person.email === recipient.email
                                            ? {
                                                ...person,
                                                delivery_email: e.target.value,
                                              }
                                            : person,
                                      ),
                                    }
                                  : row,
                              ) || null,
                          )
                        }
                      />
                    </FormControl>
                  ))}
                {outcomes[r.id] && (
                  <Alert color={outcomes[r.id].retry ? "warning" : "neutral"}>
                    <Box>
                      <Typography>{outcomes[r.id].message}</Typography>
                      {outcomes[r.id].results?.map((result) => (
                        <Typography key={result.email}>
                          {result.delivery_email || result.email}:{" "}
                          {result.message}
                        </Typography>
                      ))}
                    </Box>
                  </Alert>
                )}
              </Card>
            ))}
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Button
                variant="plain"
                disabled={!!busy}
                onClick={() => setBatch(null)}
              >
                Close review
              </Button>
              <Button
                loading={busy === "batch"}
                disabled={
                  !!busy || Object.keys(outcomes).length > 0 ||
                  (operation === "reminders" &&
                    batch.some((row) =>
                      row.recipients.some(
                        (recipient) =>
                          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                            (
                              recipient.delivery_email ?? recipient.email
                            ).trim(),
                          ),
                      ),
                    ))
                }
                color={operation === "delete" ? "danger" : "primary"}
                onClick={() => execute(false)}
              >
                {operation === "delete"
                  ? `Permanently delete ${batch.length === 1 ? "contract" : `${batch.length} contracts`}`
                  : "Confirm reviewed action"}
              </Button>
              {Object.values(outcomes).some((o) => o.retry) && (
                <Button disabled={!!busy} onClick={() => execute(true)}>
                  Retry failed only
                </Button>
              )}
            </Stack>
          </ModalDialog>
        </Modal>
      )}
    </Box>
  );
};
